import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PolicyIcon from '@mui/icons-material/Policy'
import useAuthStore from '../store/authStore'
import {
  canExportData,
  hasAdminPermission,
  PERMISSION_SUBJECTS,
} from '../utils/adminPermissions'
import { criticalAuditService } from '../services'
import { downloadBlobResponse } from '../components/price-concerns/priceConcernUtils'
import { formatDateTime } from '../utils/dateFormat'

const CATEGORY_TABS = [
  { id: 'pin', label: 'PIN' },
  { id: 'password', label: 'Password' },
  { id: 'role_change', label: 'Role changes' },
  { id: 'bulk_role_assignment', label: 'Bulk role assignment' },
  { id: 'bulk_role_creation', label: 'Bulk role creation' },
]

function summarizeMetadata(row) {
  const meta = row?.metadata || {}
  const parts = []
  if (meta.targetEmail) parts.push(`Target: ${meta.targetEmail}`)
  if (meta.beforeRoleCodes || meta.afterRoleCodes) {
    parts.push(
      `Roles: ${(meta.beforeRoleCodes || []).join(', ') || '—'} → ${(meta.afterRoleCodes || []).join(', ') || '—'}`,
    )
  }
  if (meta.code) parts.push(`Role code: ${meta.code}`)
  if (meta.display_name) parts.push(meta.display_name)
  if (meta.appUserId) parts.push(`App user #${meta.appUserId}`)
  if (meta.shopName) parts.push(`Shop: ${meta.shopName}`)
  if (meta.orgId || meta.org_id) parts.push(`Org: ${meta.orgId || meta.org_id}`)
  if (!parts.length && Object.keys(meta).length) {
    parts.push(JSON.stringify(meta))
  }
  return parts.join(' · ') || '—'
}

const CriticalAuditPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canRead = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.CRITICAL_AUDIT,
    'read',
  )
  const canExport = canExportData(adminPermissions)

  const [tabIndex, setTabIndex] = useState(0)
  const category = CATEGORY_TABS[tabIndex]?.id

  const [actorEmail, setActorEmail] = useState('')
  const [targetEmail, setTargetEmail] = useState('')
  const [orgId, setOrgId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [snackbar, setSnackbar] = useState(null)

  const queryParams = useMemo(
    () => ({
      category,
      actorEmail: actorEmail.trim() || undefined,
      targetEmail: targetEmail.trim() || undefined,
      orgId: orgId.trim() || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      page: page + 1,
      limit: rowsPerPage,
    }),
    [
      category,
      actorEmail,
      targetEmail,
      orgId,
      dateFrom,
      dateTo,
      page,
      rowsPerPage,
    ],
  )

  const loadRows = useCallback(async () => {
    if (!canRead) return
    setLoading(true)
    setError(null)
    try {
      const data = await criticalAuditService.list(queryParams)
      setRows(Array.isArray(data.items) ? data.items : [])
      setTotal(data.pagination?.total ?? 0)
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || 'Failed to load audit log',
      )
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [canRead, queryParams])

  useEffect(() => {
    loadRows()
  }, [loadRows])

  const handleExport = async () => {
    if (!canExport) return
    setExporting(true)
    try {
      const response = await criticalAuditService.exportCsv({
        category,
        actorEmail: actorEmail.trim() || undefined,
        targetEmail: targetEmail.trim() || undefined,
        orgId: orgId.trim() || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
      downloadBlobResponse(response, 'critical-audit.csv')
      setSnackbar({ severity: 'success', message: 'CSV download started.' })
    } catch (e) {
      setSnackbar({
        severity: 'error',
        message:
          e.response?.data?.message || e.message || 'Export failed',
      })
    } finally {
      setExporting(false)
    }
  }

  if (!canRead) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <PolicyIcon color="primary" />
        Critical audit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Security-sensitive admin actions: PIN changes, passwords, role
        assignments, and bulk RBAC imports.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, next) => {
            setTabIndex(next)
            setPage(0)
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {CATEGORY_TABS.map((tab) => (
            <Tab key={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          label="Actor email"
          value={actorEmail}
          onChange={(e) => {
            setActorEmail(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="Target email / phone"
          value={targetEmail}
          onChange={(e) => {
            setTargetEmail(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="Org ID"
          value={orgId}
          onChange={(e) => {
            setOrgId(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 120 }}
        />
        <TextField
          size="small"
          label="From"
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value)
            setPage(0)
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value)
            setPage(0)
          }}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" onClick={loadRows} disabled={loading}>
          Refresh
        </Button>
        {canExport ? (
          <Button
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            disabled={exporting}
            onClick={() => void handleExport()}
          >
            Export CSV
          </Button>
        ) : null}
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f7fa' }}>
              <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  No events match these filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(row.created_at)}
                  </TableCell>
                  <TableCell>{row.action}</TableCell>
                  <TableCell>{row.performed_by_email || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 480 }}>
                    {summarizeMetadata(row)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10) || 20)
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default CriticalAuditPage
